// Dedicated "how the kanban board works" walkthrough.
// Separate from /how-to-use/ (which covers the calculators / overall site).
// Lean, focused, screenshot-driven. EN-only for the Lean MVP — the existing
// /how-to-use/ FR translation continues to serve French visitors as a fallback.
import { Link } from 'react-router-dom'
import Seo, { softwareAppSchema } from '../components/Seo'
import Breadcrumbs from '../components/Breadcrumbs'
import { useLocale, localizedHref } from '../i18n/locale'

const PATH = '/kanban-board/how-it-works/'

const TITLE = 'How the Kanban Board Works — RenoSheets'
const DESC = 'A quick walkthrough of the free RenoSheets kanban board: create a board, add cards, drag them across columns, attach material sheets, and export a backup. No signup.'

// Steps shown on the page. Each `img` is the file the user will drop into
// public/images/kanban/. Built to a fixed width × height so the image swaps in
// cleanly with no layout shift even before the file exists. Until a real
// screenshot is in place, the gray placeholder shows the filename so it's
// obvious which screenshot belongs where.
interface Step {
  n: number
  title: string
  body: string
  img: string         // public path
  alt: string
  w: number
  h: number
}

const STEPS: Step[] = [
  {
    n: 1,
    title: 'Create a board — no signup',
    body: 'Open the page and you are already in. Boards live in your browser only; nothing is uploaded.',
    img: '/images/kanban/01-create-board.webp',
    alt: 'A fresh, empty RenoSheets kanban board with the four default columns: Planning, Quoted, In Progress, Done.',
    w: 1200, h: 600,
  },
  {
    n: 2,
    title: 'Add task cards',
    body: 'Click "+ Add task" in any column. Give the card a name, pick a colour, set a due date, and you are good.',
    img: '/images/kanban/02-add-card.webp',
    alt: 'The card-add form open inside the Planning column, with a name field, colour swatches, and a due-date picker.',
    w: 1000, h: 600,
  },
  {
    n: 3,
    title: 'Move cards between columns',
    body: 'Use the ← → arrow buttons on each card to step it through Planning → Quoted → In Progress → Done (and back). Drag-and-drop reorders cards up and down within the same column.',
    img: '/images/kanban/03-drag-drop.webp',
    alt: 'A task card with the left and right arrow buttons highlighted at the bottom-left, used to move the card between board columns.',
    w: 1200, h: 500,
  },
  {
    n: 4,
    title: 'Attach a saved materials sheet',
    body: 'Once you have saved your calculated sheets from the calculators you can attach them to each task within the project planner. Just click "Import from a saved sheet" from within your task and select the sheet. The materials list will then appear within that task.',
    img: '/images/kanban/04-attach-sheet.webp',
    alt: 'A card with an attached "Paint Calculator — 450 sq ft" materials sheet showing quantities for paint and primer.',
    w: 1000, h: 600,
  },
  {
    n: 5,
    title: 'Back it up or move devices',
    body: 'Export everything to a single file. Import on another browser or device to restore — your data, your control.',
    img: '/images/kanban/05-backup.webp',
    alt: 'The settings menu with the "Export backup" and "Import backup" buttons highlighted.',
    w: 1000, h: 400,
  },
]

// Image wrapper that reserves the exact aspect-ratio box so the layout never
// shifts when the real screenshot loads. `object-contain` (not cover) so the
// full screenshot is always visible — UI text and chrome must not be cropped.
// Mid-grey background fills any letterbox space when the source's aspect ratio
// doesn't match the declared w/h.
function Screenshot({ src, alt, w, h }: { src: string; alt: string; w: number; h: number }) {
  return (
    <div className="overflow-hidden rounded-lg border border-border bg-surface-2 shadow-sm" style={{ aspectRatio: `${w} / ${h}` }}>
      <img
        src={src}
        alt={alt}
        width={w}
        height={h}
        loading="lazy"
        decoding="async"
        className="h-full w-full object-contain"
        onError={(e) => {
          // Until the real screenshot is uploaded, show a labeled placeholder
          // box so the page still reads cleanly and the missing filename is
          // obvious for the asset owner.
          const el = e.currentTarget
          el.style.display = 'none'
          const parent = el.parentElement
          if (parent && !parent.querySelector('[data-placeholder]')) {
            const ph = document.createElement('div')
            ph.setAttribute('data-placeholder', 'true')
            ph.className = 'flex h-full w-full items-center justify-center bg-surface-2 p-6 text-center text-xs text-text-subtle'
            ph.innerHTML = `<span><strong>Screenshot:</strong> ${src.split('/').pop()}<br/><em class="opacity-70">${alt}</em></span>`
            parent.appendChild(ph)
          }
        }}
      />
    </div>
  )
}

export default function KanbanBoardHowItWorks() {
  const locale = useLocale()
  const L = (en: string) => localizedHref(en, locale)

  return (
    // Outer container is wide enough for big screenshots (max-w-6xl ≈ 1152px).
    // Text content stays narrower (max-w-3xl) inside the outer container for
    // readability — screenshots break out to the full width.
    <div className="mx-auto max-w-6xl px-4 py-8">
      <Seo title={TITLE} description={DESC} path={PATH} schema={softwareAppSchema('RenoSheets Kanban Board', DESC, localizedHref(PATH, locale))} />
      <div className="mx-auto max-w-3xl">
        <Breadcrumbs
          items={[
            { name: 'Home', href: L('/') },
            { name: 'Free Kanban Board', href: L('/kanban-board/') },
            { name: 'How it works', href: L(PATH) },
          ]}
        />

        <h1 className="text-3xl">How the kanban board works</h1>
        <p className="mt-3 text-text-muted">
          A quick walkthrough of the free RenoSheets kanban board — from a fresh empty board to a project you can hand off.
        </p>

        {/* Overview */}
        <section className="mt-8 rounded-2xl border border-border bg-surface p-6">
          <h2 className="text-xl font-semibold text-text">Overview</h2>
          <p className="mt-2 text-text-body">
            The board is four columns — <strong>Planning · Quoted · In Progress · Done</strong> — and a stack of cards. Move a card between columns with the ← → arrows on the card, or drag it up and down to reorder within its column. Every card holds a task name, optional colour, due date, and an attached materials sheet from any calculator. Everything is stored in your own browser — no account, no server, no limits.
          </p>
        </section>
      </div>

      {/* Steps. Each step: indented text + number on the left (narrow column),
          then a full-width screenshot below that spans the wider container. */}
      <ol className="mt-10 space-y-14">
        {STEPS.map((s) => (
          <li key={s.n}>
            <div className="mx-auto flex max-w-3xl gap-5">
              <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-green-100 text-base font-bold text-green-800 dark:bg-green-500/15 dark:text-green-300">
                {s.n}
              </div>
              <div className="min-w-0 flex-1">
                <h2 className="text-lg font-semibold text-text">{s.title}</h2>
                <p className="mt-1.5 text-text-muted">{s.body}</p>
              </div>
            </div>
            <div className="mt-5">
              <Screenshot src={s.img} alt={s.alt} w={s.w} h={s.h} />
            </div>
          </li>
        ))}
      </ol>

      {/* CTA */}
      <div className="mx-auto mt-14 max-w-3xl rounded-2xl border border-border bg-brand-tint/30 p-7 text-center">
        <h2 className="text-xl font-semibold text-text">Ready to try it?</h2>
        <p className="mt-2 text-text-muted">Open the board in your browser — no signup, free forever.</p>
        <div className="mt-5 flex flex-wrap justify-center gap-3">
          <Link to={L('/kanban-board/')} className="rounded-lg bg-primary px-6 py-3 font-semibold text-on-primary hover:opacity-90">
            Start a new project planner →
          </Link>
          <Link to={L('/how-to-use/')} className="rounded-lg border-2 border-brand px-6 py-3 font-semibold text-brand-text hover:bg-brand-tint">
            Calculator how-to
          </Link>
        </div>
      </div>
    </div>
  )
}
